// Methods to generically handle getting filtered, sorted, and paginated rows for a table
// Typical use to to call in the order they are declared below, passing the resulting rows to the next function
// Sort -> Filter -> PageAmount -> PageItems

import { Row } from '@harmony/enablers/components/data-grid/data-grid';
import { useEffect, useMemo, useState } from 'react';
import { TTableFilters } from './table-filters-panel';

export const useFilteredRows = (rows: Row<any>[], searchText: string, tableFilters?: TTableFilters): Row<any>[] =>
  useMemo(
    () =>
      rows.filter((row) => {
        const filterHit = (): boolean => {
          let filterCheck = true;
          const tableSpecificFilters = tableFilters || {};
          const filteredColumns = Object.keys(tableSpecificFilters || {}).filter((column) => Object.keys(tableSpecificFilters[column]).length > 0);
          filteredColumns.forEach((column) => {
            filterCheck = filterCheck && tableSpecificFilters[column][row.cells[column]];
          });
          return filterCheck;
        };
        const searchHit = (): boolean =>
          Object.values(row.cells).some((cellValue: any) => `${cellValue}`.toLowerCase().includes(searchText.toLowerCase()));

        return filterHit() && searchHit();
      }),
    [rows, searchText, tableFilters]
  );

// TODO: Update this to handle date sorting
export const useGetSortedRows = (rows: Row<any>[], sortBy: string | undefined, sortAscending: boolean): Row<any>[] =>
  useMemo(() => {
    if (!sortBy) {
      return rows;
    }

    return [
      ...rows.sort((a, b) => {
        let firstValue: string | number;
        let secondValue: string | number;

        firstValue = a.cells[sortBy].replaceAll(',', '');
        secondValue = b.cells[sortBy].replaceAll(',', '');

        if (Number(firstValue) && Number(secondValue)) {
          firstValue = Number(firstValue);
          secondValue = Number(secondValue);
        }

        if (firstValue > secondValue) {
          return sortAscending ? 1 : -1;
        }
        if (firstValue < secondValue) {
          return sortAscending ? -1 : 1;
        }
        return 0;
      }),
    ];
  }, [rows, sortBy, sortAscending]);

export const useGetPageAmount = (rows: Row<any>[], pageSize: number): number => {
  const [pageCount, setPageCount] = useState<number>(1);
  useEffect(() => {
    setPageCount(Math.ceil(rows.length / pageSize));
  }, [rows.length, pageSize]);
  return pageCount;
};

export const useGetPageItems = (rows: Row<any>[], currentPage: number, pageSize: number): Row<any>[] =>
  useMemo(() => rows.slice((currentPage - 1) * pageSize, currentPage * pageSize), [currentPage, pageSize, rows]);
